<script setup lang="ts">
import { ElIcon } from 'element-plus'

import { BoxComponent, CheckboxComponent, CountBadgeComponent, FlexComponent, TypographyComponent } from '..'
import ChevronIcon from '../../assets/icons/chevron.svg?component'
import type { ListItems } from '../../types/common.types'

const props = withDefaults(
  defineProps<{
    label: string
    options: ListItems
    checkedValues?: string[]
    open?: boolean
    selected?: boolean
  }>(),
  {
    checkedValues: () => [],
    open: false,
    selected: false,
  },
)

const emits = defineEmits<{
  (e: 'activate'): void
  (e: 'open'): void
  (e: 'close'): void
  (e: 'toggle', value: string): void
}>()

const isChecked = (value: string) => props.checkedValues.includes(value)
</script>

<template>
  <div
    class="nested-option-row"
    role="button"
    tabindex="0"
    aria-haspopup="menu"
    :aria-expanded="open"
    @click.stop="emits('activate')"
    @keydown.enter.stop.prevent="emits('activate')"
    @keydown.right.stop.prevent="emits('open')"
    @keydown.left.stop.prevent="emits('close')"
    @keydown.esc.stop.prevent="emits('close')"
    @mouseenter="emits('open')"
    @mouseleave="emits('close')"
  >
    <BoxComponent :p="16" w="100%" class="nested-option-row__surface">
      <FlexComponent flex="1" align="center" justify="space-between" :gap="12">
        <TypographyComponent :variant="selected ? 'title-large' : 'body-large'">
          {{ label }}
        </TypographyComponent>
        <FlexComponent align="center" :gap="8">
          <CountBadgeComponent :count="checkedValues.length" />
          <ElIcon :size="24" class="nested-option-row__chevron">
            <ChevronIcon />
          </ElIcon>
        </FlexComponent>
      </FlexComponent>
    </BoxComponent>
    <div v-if="open" class="nested-option-row__submenu" role="menu" @click.stop>
      <div v-for="option in options" :key="option.value" class="nested-option-row__submenu-item">
        <CheckboxComponent :model-value="isChecked(option.value)" @change="emits('toggle', option.value)">
          {{ option.label }}
        </CheckboxComponent>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.nested-option-row {
  position: relative;
  width: 100%;
  cursor: pointer;
}

.nested-option-row:focus-visible {
  outline: none;
}

/* Separate from the submenu so an end row can clip the state layer without clipping the submenu. */
.nested-option-row__surface {
  clip-path: var(--option-end-clip, none);
}

.nested-option-row:hover .nested-option-row__surface,
.nested-option-row:focus-visible .nested-option-row__surface {
  background: rgba(34, 34, 34, 0.12);
}

.nested-option-row__chevron {
  transform: rotate(-90deg);
  /* Resets the 5px right margin Element Plus gives every `i` inside a dropdown item. */
  margin-right: 0;
}

.nested-option-row__submenu {
  position: absolute;
  top: 0;
  left: 100%;
  z-index: 1;
  width: 160px;
  padding: 8px 0;
  border-radius: 16px;
  background: var(--inverted);
  box-shadow: 0 2px 8px 0 rgba(60, 75, 93, 0.16);
  overflow: hidden;
  cursor: default;
}

.nested-option-row__submenu-item {
  display: flex;
  align-items: center;

  &:hover {
    background: rgba(34, 34, 34, 0.12);
  }

  :deep(.el-checkbox) {
    display: flex;
    width: 100%;
    height: auto;
    padding: 16px;
    margin-right: 0;
  }

  :deep(.el-checkbox__label) {
    padding-left: 8px;
    font-family: inherit;
    font-weight: 400;
  }
}
</style>
